import { NpiChecklistItem } from "../../../../../models/npi/npi-checklist";
import type { ChecklistSection } from "../types/npi-checklist.types";
import { CHECKLIST_PHASE_LEGEND_ITEMS } from "../constants/dashboard.constants";

const PHASE_SEPARATOR = " - ";

const DESCRIPTION_SEPARATORS = ["\n", PHASE_SEPARATOR];

const parseSectionName = (description: string, previousPhaseIndex: number): { name: string; phaseIndex: number } => {
  const [prefix, ...rest] = description.split(PHASE_SEPARATOR);
  const phaseIndex = CHECKLIST_PHASE_LEGEND_ITEMS.findIndex((phase) => phase.label.toUpperCase() === prefix.trim().toUpperCase());
  return phaseIndex >= 0 && rest.length
    ? { name: rest.join(PHASE_SEPARATOR), phaseIndex }
    : { name: description, phaseIndex: previousPhaseIndex };
};

export const splitItemDescription = (description: string): { title: string; detail: string } => {
  const positions = DESCRIPTION_SEPARATORS.map((separator) => ({ separator, index: description.indexOf(separator) })).filter(
    ({ index }) => index > 0,
  );
  if (!positions.length) {
    return { title: description.trim(), detail: "" };
  }
  const { separator, index } = positions.reduce((first, current) => (current.index < first.index ? current : first));
  return {
    title: description.slice(0, index).trim(),
    detail: description.slice(index + separator.length).replace(/\s+/g, " ").trim(),
  };
};

const isHeaderRow = (item: NpiChecklistItem) => !item.owner && !item.due_date && !item.status;

const isActivity = (item: NpiChecklistItem) => item.item.includes(".") && !isHeaderRow(item);

// Monta a hierarquia pelo código: "1" (seção) > "1.1" (subseção) > "1.1.01" (item)
export const buildChecklistSections = (items: NpiChecklistItem[]): ChecklistSection[] => {
  const sections: ChecklistSection[] = [];

  for (const item of items) {
    const section = sections[sections.length - 1];

    if (!item.item.includes(".")) {
      sections.push({ code: item.item, ...parseSectionName(item.description, section?.phaseIndex ?? -1), subsections: [] });
    } else if (!section) {
      continue;
    } else if (isHeaderRow(item)) {
      section.subsections.push({ code: item.item, name: item.description, items: [] });
    } else {
      if (!section.subsections.length) {
        section.subsections.push({ code: "", name: "", items: [] });
      }
      section.subsections[section.subsections.length - 1].items.push(item);
    }
  }

  return sections;
};

export const getChecklistOwners = (items: NpiChecklistItem[]): string[] =>
  Array.from(new Set(items.filter((item) => isActivity(item) && item.owner).map((item) => item.owner))).sort();
